import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import { urls } from '@repo/app-constants';
import { kitResultsViewable, parseDate } from '@repo/utils';
import { Kit, KitStatuses, KitType } from '@repo/types';

import useUpdateKitDates from './useUpdateKitDates';
import { getSortedKitsForTinyAccountId } from 'store/kits';
import { getTinyAccountById } from 'store/account';
import { getImpersonateId } from 'store/user';
import { usePortalConfigs } from './usePortalConfigs';
import useSubscriptions from 'services/general/subscriptionsList';
import useFeature from 'services/features/useFeature';
import { useShouldShowConsults } from './useShouldShowConsultLink';

import { CheckIcon } from 'views/Information/NextStep';
import { HomeResultsStatus } from 'views/Home/HomeContent/HomeNextSteps/HomeNextSteps';
import { ButtonSizes, ButtonColors, LinkButton } from 'components';

const RESULTS_SENT_TO_PRACTITIONER_STEP_TITLE = 'Results sent to practitioner';

type HomeStep = {
  title: string;
  isActive: boolean;
  Status: JSX.Element;
  clickable?: {
    text: string;
    url: string;
    onClick?: () => Promise<void>;
  };
  isBlocking?: boolean;
  descriptionBlocking?: string;
  isCompleted?: boolean;
  inlineLinkPostCompletion?: {
    text: string;
    url: string;
  };
  inlineLinkWhileBlocking?: {
    url: string;
  };
  completedContent?: React.ReactNode;
  consultLink?: {
    prefixText: string;
    text: string;
    url: string;
  };
  description?: string;
};

export const useTinyAccountForHome = (tinyAccountId: string | null) => {
  const impersonateId = useSelector(getImpersonateId);
  const tinyAccount = useSelector(getTinyAccountById(tinyAccountId));
  const kits = useSelector(getSortedKitsForTinyAccountId(tinyAccountId));
  const hasMultipleKits = kits?.length > 1;
  const { isAnyTinyPlus } = useSubscriptions();
  const hasMembership = isAnyTinyPlus;
  const kitMutation = useUpdateKitDates({ onlyOptimistic: true, silent: true });

  const noVoidKits = useMemo(
    () => kits?.filter(kit => kit.status !== KitStatuses.VOID),
    [kits],
  );
  const latestKit = useMemo(
    () => (noVoidKits?.length ? noVoidKits[0] : null),
    [noVoidKits],
  );

  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs(latestKit);
  const { isFeatureEnabled: showConsultCTA } = useFeature(
    'next_step_consult_cta',
  );
  const { isFeatureEnabled: resultsHomeFormsCompletion } = useFeature(
    'results_home_forms_completion',
  );

  const viewableKits = useMemo(() => {
    if (!latestKit) return [];
    const resultsViewableKits = noVoidKits?.filter(
      kit =>
        kitResultsViewable(kit, !!impersonateId) && !!kit.date_survey_completed,
    );
    const latestGutTest = noVoidKits?.find(
      kit => kit.sampling_type === KitType.STOOL,
    );
    const latestVaginalTest = noVoidKits?.find(
      kit => kit.sampling_type === KitType.VAGINAL,
    );
    //check if there are 2 "latest" kits (gut and vaginal), if not we just return the viewable kits as normal
    if (!latestGutTest || !latestVaginalTest) {
      return resultsViewableKits;
    }
    //we add to the kits list the one that is not the absolute latest if it doesn't show results yet
    if (
      latestGutTest.id === latestKit.id &&
      !kitResultsViewable(latestVaginalTest, !!impersonateId)
    ) {
      return [latestVaginalTest, ...resultsViewableKits];
    }
    if (
      latestVaginalTest.id === latestKit.id &&
      !kitResultsViewable(latestGutTest, !!impersonateId)
    ) {
      return [latestGutTest, ...resultsViewableKits];
    }
    return resultsViewableKits;
  }, [noVoidKits, latestKit, impersonateId]);

  // Use centralized hook to check if consult links should be shown
  // This handles: hide_consults flags with credits override, existing consults, survey & results ready
  const shouldShowConsultsFromHook = useShouldShowConsults(latestKit);

  const surveyType = portalConfig.survey_type ?? 'full';
  const hideSurveyStep = surveyType === 'skip' || surveyType === 'short';

  //STEPS LOGIC
  const stepsList = useMemo(() => {
    //..CouldBeActive means the requirements of the step alone are met
    //..IsActive means the step is also not blocked by previous steps
    const activateIsActive = !latestKit; //it is not blocked by Create profile

    const infoCouldBeActive = !latestKit?.date_survey_completed;
    const infoIsActive = hideSurveyStep
      ? false
      : infoCouldBeActive && !activateIsActive;
    const surveyIncomplete =
      !hideSurveyStep && !!latestKit && !latestKit?.date_survey_completed;
    const infoIsBlocking = hideSurveyStep
      ? false
      : resultsHomeFormsCompletion
        ? surveyIncomplete
        : !latestKit?.date_survey_completed &&
          !!latestKit &&
          kitResultsViewable(latestKit, !!impersonateId);

    const resultsCouldBeActive =
      !!latestKit &&
      kitResultsViewable(latestKit, !!impersonateId) &&
      !latestKit?.date_results_step_clicked;
    const resultsIsActive = resultsCouldBeActive && !infoIsActive;
    const resultsIsCompleted =
      !!latestKit &&
      kitResultsViewable(latestKit, !!impersonateId) &&
      !!latestKit?.date_results_step_clicked;

    const hasPractitioner = !!latestKit?.practitioner_id;
    const resultsSentToPractitionerIsCompleted =
      !!latestKit?.date_results_ready_practitioner;
    const resultsSentToPractitionerCouldBeActive =
      hasPractitioner &&
      !!latestKit?.date_survey_completed &&
      !kitResultsViewable(latestKit, !!impersonateId) &&
      !resultsSentToPractitionerIsCompleted;
    const resultsSentToPractitionerIsActive =
      resultsSentToPractitionerCouldBeActive &&
      !infoIsActive &&
      !activateIsActive;

    // Consult step logic - combine hook result with feature flag
    const shouldShowConsultLink = showConsultCTA && shouldShowConsultsFromHook;

    const actionsCouldBeActive =
      !latestKit?.date_actions_step_clicked &&
      !!latestKit?.date_results_step_clicked;
    const actionsIsActive =
      actionsCouldBeActive &&
      resultsIsCompleted &&
      !resultsIsActive &&
      !infoIsBlocking &&
      !infoIsActive &&
      !activateIsActive;
    const trackIsActive =
      !!latestKit?.date_results_step_clicked &&
      !!latestKit?.date_actions_step_clicked &&
      !actionsIsActive &&
      resultsIsCompleted &&
      !resultsIsActive &&
      !infoIsBlocking &&
      !infoIsActive &&
      !activateIsActive;

    const getResultStatus = () => {
      if (!latestKit) return <CheckIcon isCompleted={false} />;
      const latestKitResultsViewable = kitResultsViewable(
        latestKit,
        !!impersonateId,
      );
      if (latestKit?.date_in_transit_to_lab && !latestKitResultsViewable)
        return <HomeResultsStatus text='Lab analysis' />;
      if (latestKitResultsViewable && !latestKit?.date_results_step_clicked)
        return <HomeResultsStatus text='Ready' />;
      if (latestKitResultsViewable && latestKit?.date_results_step_clicked)
        return <CheckIcon isCompleted={true} />;
      return <CheckIcon isCompleted={false} />;
    };

    const updateKitDate = async (key: keyof Kit) => {
      //only update if the key doesn't have a date already set
      if (!latestKit || !!latestKit[key]) return;
      const editObject = {
        id: latestKit?.id,
        [key]: format(new Date(), 'yyyy-MM-dd'),
      };
      await kitMutation.mutateAsync(editObject);
    };

    if (!tinyAccount) return [];
    const steps: HomeStep[] = [
      {
        title: 'Activate kit',
        isActive: activateIsActive,
        Status: <CheckIcon isCompleted={!!latestKit} />,
        clickable: {
          text: 'Activate Kit',
          url: '/activate',
        },
      },
      ...(hideSurveyStep
        ? []
        : [
            {
              title: resultsHomeFormsCompletion
                ? 'Complete health forms'
                : 'Tell us your health story',
              isActive: infoIsActive,
              Status: (
                <CheckIcon
                  isCompleted={!!latestKit?.date_survey_completed}
                  isBlocked={infoIsBlocking}
                />
              ),
              clickable: {
                text: resultsHomeFormsCompletion
                  ? 'Complete health forms'
                  : 'Complete info',
                url: `/information/${latestKit?.id}`,
              },
              isBlocking: infoIsBlocking,
              descriptionBlocking: resultsHomeFormsCompletion
                ? kitResultsViewable(latestKit ?? undefined, !!impersonateId)
                  ? 'Your sample has been processed but we need your forms to create your report.'
                  : 'Required to create report'
                : `Our lab has finished processing your sample but we're not able to publish your report until all surveys are completed.`,
              isCompleted: !!latestKit?.date_survey_completed,
              inlineLinkPostCompletion: {
                text: 'View',
                url: `/results/${latestKit?.id}/surveys`,
              },
              inlineLinkWhileBlocking: resultsHomeFormsCompletion
                ? { url: `/information/${latestKit?.id}` }
                : undefined,
              completedContent:
                resultsHomeFormsCompletion &&
                !!latestKit?.date_survey_completed ? (
                  <LinkButton
                    label='Edit answers'
                    href={`/results/${latestKit?.id}/surveys`}
                    state={{ from: '/' }}
                    size={ButtonSizes.COMPACT}
                    variant='outline'
                    color={ButtonColors.PURPLE}
                    iconNameLeft='edit01'
                  />
                ) : undefined,
            },
          ]),
      ...(hasPractitioner
        ? [
            {
              title: RESULTS_SENT_TO_PRACTITIONER_STEP_TITLE,
              isActive: resultsSentToPractitionerIsActive,
              Status: (
                <CheckIcon isCompleted={resultsSentToPractitionerIsCompleted} />
              ),
              isCompleted: resultsSentToPractitionerIsCompleted,
            } satisfies HomeStep,
          ]
        : []),
      {
        title: 'Review results',
        isActive: resultsIsActive,
        Status: getResultStatus(),
        clickable: {
          text: 'View results',
          url: `/results/${latestKit?.id}`,
          onClick: () => updateKitDate('date_results_step_clicked'),
        },
        isCompleted:
          !!latestKit &&
          kitResultsViewable(latestKit, !!impersonateId) &&
          !!latestKit?.date_results_step_clicked,
        inlineLinkPostCompletion: {
          text: 'View',
          url: `/results/${latestKit?.id}`,
        },
        consultLink: shouldShowConsultLink
          ? {
              prefixText: 'Need help understanding your results?',
              text: 'Meet with a specialist',
              url: `/results/${latestKit?.id}/consults/select-method`,
            }
          : undefined,
      },
      {
        title: 'Implement Action Plan',
        isActive: actionsIsActive,
        Status: (
          <CheckIcon isCompleted={!!latestKit?.date_actions_step_clicked} />
        ),
        clickable: {
          text: 'Take Action',
          url: `/action-plan/${latestKit?.id}`,
          onClick: () => updateKitDate('date_actions_step_clicked'),
        },
        isCompleted: !!latestKit?.date_actions_step_clicked,
        inlineLinkPostCompletion: {
          text: 'View',
          url: `/action-plan/${latestKit?.id}`,
        },
      },
    ];

    // Only show the 'Track your progress' step if user has membership OR hide_upsells is not enabled
    if (
      hasMembership ||
      (!portalConfig.hide_upsells && !isPortalConfigLoading)
    ) {
      steps.push({
        title: hasMembership ? 'Take next sample' : 'Track your progress',
        isActive: trackIsActive,
        Status: <CheckIcon isCompleted={false} />,
        clickable: hasMembership
          ? {
              text: 'Activate Kit',
              url: '/activate',
            }
          : {
              text: 'Explore membership',
              url: urls.MEMBERSHIP,
            },
        description: tinyAccount?.next_sample_date
          ? `We recommend you resample on ${format(
              parseDate(tinyAccount?.next_sample_date) ?? new Date(),
              'MMM d, yyyy',
            )}`
          : '',
      });
    }

    return steps;
  }, [
    tinyAccount,
    latestKit,
    hasMembership,
    impersonateId,
    portalConfig.hide_upsells,
    hideSurveyStep,
    isPortalConfigLoading,
    showConsultCTA,
    resultsHomeFormsCompletion,
    shouldShowConsultsFromHook,
  ]);

  const getActiveStep = () => {
    // The survey slot is the active step while forms are pending, even when hidden
    if (latestKit && !latestKit.date_survey_completed) return 1;

    const activeIndex = stepsList.findIndex(step => step.isActive);
    if (activeIndex === -1) return -1;

    // Preserve RecommendedForYou step mapping by hiding the practitioner-only step
    // and re-inserting the survey slot when that step is hidden
    const practitionerStepIndex = stepsList.findIndex(
      step => step.title === RESULTS_SENT_TO_PRACTITIONER_STEP_TITLE,
    );
    const step =
      practitionerStepIndex !== -1 && activeIndex >= practitionerStepIndex
        ? activeIndex - 1
        : activeIndex;
    return hideSurveyStep && step >= 1 ? step + 1 : step;
  };

  const getDisplayName = useCallback(
    (useLowercase?: boolean) => {
      const displayName = tinyAccount?.first_name
        ? `${tinyAccount?.first_name}'s`
        : 'Your';
      return useLowercase && !tinyAccount?.first_name
        ? displayName.toLowerCase()
        : displayName;
    },
    [tinyAccount?.first_name],
  );

  return {
    tinyAccount,
    hasMultipleKits,
    kits,
    noVoidKits,
    viewableKits,
    latestKit,
    getDisplayName,
    hasMembership,
    stepsList,
    getActiveStep,
  };
};
